import { useEffect, useRef, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { logError, LogCategory } from "utils/sentryLogger";
import { request} from "hooks/useOkoApi/useOkoApi";
import { colors, DEFAULT_EXPLANATION_IMAGE_PROMPT } from "utils";

export const ExplanationImage = ({ imagePrompt, isParentLoading }) => {
  const [formData, setFormData] = useState({ image_llm_provider: "openai", image_prompt: imagePrompt });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState();
  const imagePromptTextareaRef = useRef(null);
  const [imageBase64, setImageBase64] = useState(null);

  const alertStyle = {
    padding: "8px",
    marginBottom: 0,
    marginTop: "16px"
  };

  const adjustImagePromptHeight = () => {
    if (imagePromptTextareaRef.current) {
      const textarea = imagePromptTextareaRef.current;
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  const handleChange = (e) => {
    if (isLoading || isParentLoading) return;
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    if (name === "image_prompt") {
      adjustImagePromptHeight();
    }
  };

  const applyDefaultImagePrompt = (e) => {
    if (isLoading || isParentLoading) return;
    e.preventDefault();
    if (formData.image_prompt.startsWith(DEFAULT_EXPLANATION_IMAGE_PROMPT)) return;
    setFormData((prevState) => ({
      ...prevState,
      image_prompt: DEFAULT_EXPLANATION_IMAGE_PROMPT + prevState.image_prompt,
    }));
    setTimeout(() => adjustImagePromptHeight(), 10);
  };

  useEffect(() => {
    adjustImagePromptHeight();
  }, []);

  const handleGenerateImage = async (e) => {
    if (isLoading || isParentLoading) return;
    e.preventDefault();
    setImageBase64(null);
    setIsLoading(true);
    setError(null);
    await request(`explanation/generate_explanation_image`, {
      payload: {
        image_llm_provider: formData.image_llm_provider.toLowerCase(),
        image_prompt: formData.image_prompt,
        image_model: formData.image_model,
      }
    }).then((response) => {
      setIsLoading(false);
      setImageBase64(response);
    }).catch((e) => {
      console.log(e);
      setIsLoading(false);
      logError("Admin explanation image operation failed", e, { category: LogCategory.ERROR });
      const errors = {};
      errors["generation"] = e?.detail || e?.message || "An unknown error occurred.";
      setError(errors);
    });
  };

  return (
    <div style={{
      display: "flex",
      backgroundColor: colors.milkGlass200,
      flexDirection: "column",
      gap: "24px" }}>
      <div style={{ display: "flex", flexDirection: "column" , gap: "24px" }}>
        <Form onSubmit={handleGenerateImage}>
          <div style={{ display: "flex", flexDirection: "column" , gap: "32px" }}>
            <Form.Group>
              <Form.Label style={{ fontWeight: "bold" }}>Image Prompt&nbsp;
                <a href="#" onClick={applyDefaultImagePrompt}>(Click here to prepend with default image prompt for better results)
                </a>
              </Form.Label>
              <Form.Control
                ref={imagePromptTextareaRef}
                required
                as="textarea"
                placeholder="Enter image prompt"
                value={formData.image_prompt}
                name="image_prompt"
                onChange={handleChange}
                style={{ overflow: "hidden" }}
              />
              {error?.image_prompt && (
                <Alert style={alertStyle} variant="danger">{error.image_prompt}</Alert>
              )}
            </Form.Group>
            <div style={{ display: "flex", gap: "32px" }}>
              <Form.Group style={{ width: "100%" }}>
                <Form.Label style={{ fontWeight: "bold" }}>Image LLM Provider</Form.Label>
                <Form.Control
                  required
                  as="select"
                  value={formData.image_llm_provider}
                  name="image_llm_provider"
                  onChange={handleChange}
                >
                  <option value="">Select image LLM provider</option>
                  <option value="openai">OpenAI</option>
                  <option value="google">Google</option>
                </Form.Control>
                {error?.image_llm_provider && (
                  <Alert style={alertStyle} variant="danger">{error.image_llm_provider}</Alert>
                )}
              </Form.Group>
              <Form.Group style={{ width: "100%" }}>
                <Form.Label style={{ fontWeight: "bold" }}>Image LLM Name</Form.Label>
                <Form.Control
                  required
                  type="text"
                  placeholder="gpt-image-1, dall-e-3, dall-e-2"
                  value={formData.image_model}
                  name="image_model"
                  onChange={handleChange}
                />
                {error?.image_model && (
                  <Alert style={alertStyle} variant="danger">{error.image_model}</Alert>
                )}
              </Form.Group>
            </div>
            {error?.generation && (
              <Alert style={alertStyle} variant="danger">{error.generation}</Alert>
            )}
            <Button style={{
              backgroundColor: colors.nightlyWoods500,
              border: "none",
              borderRadius: "6px",
              fontSize: "16px",
              height: "40px",
              padding: "0 16px" }}
                    disabled={isLoading}
                    type="submit">
              Generate image
            </Button>
          </div>
        </Form>
      </div>
      {isLoading && <div style={{ alignSelf: "center", position: "fixed", top: "50%" }}>
        <FontAwesomeIcon
          icon={faSpinner}
          spin
          size="5x"
        />
      </div>}
      {imageBase64 && <img src={`data:image/jpeg;base64,${imageBase64}`}
                           alt="Generated image"
                           style={{ alignSelf: "center", maxWidth: "1024px" }}
      />}
    </div>
  );
};
